import React, { useState, useRef } from 'react';
import { Bell } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { cn } from '../../lib/utils';

export function NotificationBell({ 
  notifications = [], 
  count = 0, 
  onMarkAsRead,
  onViewChallenge 
}) {
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef(null);

  // Note: We intentionally do NOT close on click outside
  // Notifications stay visible until user presses "Got It"

  const handleToggle = () => {
    const newOpen = !isOpen;
    setIsOpen(newOpen);
    // Mark all as read when opening
    if (newOpen && count > 0 && onMarkAsRead) {
      onMarkAsRead();
    }
  };

  const handleGotIt = () => {
    setIsOpen(false);
  };

  const handleViewChallenge = (challengeCode) => {
    onViewChallenge?.(challengeCode);
  };

  return (
    <div className="relative">
      <Button 
        ref={buttonRef}
        variant="ghost" 
        size="icon"
        className="relative"
        data-testid="notification-bell-btn"
        onClick={handleToggle}
      >
        <Bell className="h-5 w-5" />
        {count > 0 && (
          <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-destructive text-destructive-foreground text-xs font-bold flex items-center justify-center animate-pulse">
            {count > 9 ? '9+' : count}
          </span>
        )}
      </Button>
      
      {isOpen && (
        <div 
          className="fixed sm:absolute right-2 sm:right-0 left-2 sm:left-auto top-16 sm:top-full sm:mt-2 w-auto sm:w-80 max-w-[calc(100vw-16px)] bg-popover border rounded-md shadow-lg z-50"
          data-testid="notification-dropdown"
        >
          <div className="p-3 border-b">
            <h3 className="font-semibold text-sm">Notifications</h3>
          </div>
          <div className="max-h-[60vh] sm:max-h-80 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="p-6 text-center text-muted-foreground">
                <Bell className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No notifications yet</p>
                <p className="text-xs mt-1">You'll see updates here when friends play your challenges</p>
              </div>
            ) : (
              <div className="divide-y">
                {notifications.map((notification) => (
                  <div 
                    key={notification.id}
                    className={cn(
                      "p-3 hover:bg-muted/50 cursor-pointer transition-colors",
                      !notification.is_read && "bg-primary/5",
                      notification.beat_creator && "bg-destructive/5 border-l-2 border-l-destructive"
                    )}
                    data-testid={`notification-item-${notification.id}`}
                    onClick={() => handleViewChallenge(notification.challenge_code)}
                  >
                    <div className="flex items-start gap-3">
                      {!notification.is_read && (
                        <span className="h-2 w-2 mt-2 rounded-full bg-primary flex-shrink-0" />
                      )}
                      {notification.image_url && (
                        <img 
                          src={notification.image_url} 
                          alt="Challenge" 
                          className="w-10 h-10 rounded-lg object-cover"
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className={cn("text-sm", !notification.is_read ? "font-semibold" : "font-medium text-muted-foreground")}>
                          {notification.beat_creator ? (
                            <>
                              <span className="text-destructive">{notification.player_name}</span> beat your score!
                            </>
                          ) : (
                            <>
                              <span className={!notification.is_read ? "text-primary" : ""}>{notification.player_name}</span> played your challenge
                            </>
                          )}
                        </p>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="outline" className="text-xs">
                            {notification.challenge_code}
                          </Badge>
                          <span className={cn(
                            "text-xs",
                            notification.beat_creator ? "text-destructive font-medium" : "text-muted-foreground"
                          )}>
                            {Math.round(notification.score)} pts
                            {notification.beat_creator && notification.creator_score && (
                              <span className="text-muted-foreground ml-1">
                                (was {Math.round(notification.creator_score)})
                              </span>
                            )}
                          </span>
                        </div>
                        {notification.created_at && (
                          <p className="text-xs text-muted-foreground mt-1">
                            {formatRelativeTime(notification.created_at)}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          {/* Got It button to dismiss */}
          <div className="p-3 border-t bg-muted/30">
            <Button 
              variant="secondary" 
              size="sm" 
              className="w-full"
              onClick={handleGotIt}
              data-testid="notification-got-it-btn"
            >
              Got It
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// Helper to format relative time
function formatRelativeTime(dateString) {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}
